import { definedMap, dontAwait } from "@glideapps/ts-necessities";
import { asDate, asMaybeBoolean, asMaybeNumber } from "common/components/component-helpers";
import { ActionSource, LOADING } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { convertGlideDurationToJS, convertJSDurationToGlide, defined, getNowTimeZoneAgnostic } from "common/Support";
import { useForce } from "hooks/use-force";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import * as React from "react";
import Stopwatch from "../ui/views/stopwatch/stopwatch";

export const StopwatchMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[startTimeValue, durationValue, onChangeValue, isEnabled], [startTimeVP, durationVP]] =
        useForceValueProducers(getValueProducers, force, ctx);
    const { onClick } = useOnClickFromAction(onChangeValue, ActionSource.ImagePicker, ctx, env);

    const startTime = asDate(startTimeValue);
    const duration = asMaybeNumber(durationValue);

    const getElapsed = React.useCallback((): number | undefined => {
        if (startTime === undefined) return undefined;
        return getNowTimeZoneAgnostic().getTime() - startTime.getTime();
    }, [startTime]);

    const runAction = React.useCallback(() => {
        if (onClick !== undefined) {
            dontAwait(onClick(undefined));
        }
    }, [onClick]);

    const onStartResume = React.useCallback(() => {
        defined(startTimeVP).set(getNowTimeZoneAgnostic());
        runAction();
    }, [runAction, startTimeVP]);

    if (startTimeValue === LOADING || durationValue === LOADING) return env.renderProgress();
    if (startTimeVP === undefined || durationVP === undefined) return null;

    const enabled = asMaybeBoolean(isEnabled) ?? true;

    const onPause = () => {
        const elapsed = getElapsed();
        if (elapsed === undefined) return;

        startTimeVP.set("");
        durationVP.set((duration ?? 0) + convertJSDurationToGlide(elapsed));
        runAction();
    };

    const onReset = () => {
        startTimeVP.set("");
        durationVP.set("");
        runAction();
    };

    return (
        <Stopwatch
            startTimeValue={startTime}
            durationValue={definedMap(duration, convertGlideDurationToJS)}
            onStartResume={onStartResume}
            onPause={onPause}
            onReset={onReset}
            enabled={enabled}
        />
    );
};
